import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Divider,
  Tooltip,
  Typography,
  Popconfirm,
  Empty,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TrophyOutlined,
  FireOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  DollarOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { gamificationApi } from '../../../shared/api/gamificationApi';
import { useTheme } from '../../../contexts/ThemeContext';
import { Level } from './types';

const { Text, Title } = Typography;
const { TextArea } = Input;

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const LevelsTab: React.FC = () => {
  const { theme } = useTheme();
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);
  const [form] = Form.useForm();
  const [configForm] = Form.useForm();

  // States locales para controlar los switches
  const [levelsEnabled, setLevelsEnabled] = useState(true);
  const [autoLevelUp, setAutoLevelUp] = useState(true);

  useEffect(() => {
    loadLevels();
    loadConfig();
  }, []);

  const loadLevels = async () => {
    try {
      setLoading(true);
      const data = await gamificationApi.getLevels();
      setLevels(data);
    } catch (error) {
      message.error('Error al cargar niveles');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const config = await gamificationApi.getConfig();
      const levelsValue = config.levelsEnabled ?? true;
      const autoValue = config.autoLevelUp ?? true;

      setLevelsEnabled(levelsValue);
      setAutoLevelUp(autoValue);

      configForm.setFieldsValue({
        levelsEnabled: levelsValue,
        autoLevelUp: autoValue,
      });
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const handleConfigChange = async (field: string, value: boolean) => {
    try {
      // Actualizar state local inmediatamente
      if (field === 'levelsEnabled') setLevelsEnabled(value);
      if (field === 'autoLevelUp') setAutoLevelUp(value);

      await gamificationApi.updateConfig({ [field]: value });
      message.success('Configuración actualizada');
    } catch (error) {
      message.error('Error al actualizar la configuración');
      console.error(error);
      // Revertir en caso de error
      if (field === 'levelsEnabled') setLevelsEnabled(!value);
      if (field === 'autoLevelUp') setAutoLevelUp(!value);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setEditingLevel(null);
    setModalVisible(true);
  };

  const handleEdit = (level: Level) => {
    setEditingLevel(level);
    form.setFieldsValue(level);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await gamificationApi.deleteLevel(id);
      message.success('Nivel eliminado exitosamente');
      loadLevels();
    } catch (error) {
      message.error('Error al eliminar nivel');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingLevel) {
        await gamificationApi.updateLevel(editingLevel.id, values);
        message.success('Nivel actualizado exitosamente');
      } else {
        await gamificationApi.createLevel(values);
        message.success('Nivel creado exitosamente');
      }
      setModalVisible(false);
      loadLevels();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Error al guardar nivel');
    }
  };

  // Ordenar niveles por número
  const sortedLevels = [...levels].sort((a, b) => a.levelNumber - b.levelNumber);

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Configuración de Niveles */}
        <Card
          title="🏆 Sistema de Niveles"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Switch
                checked={levelsEnabled}
                onChange={(checked) => {
                  handleConfigChange('levelsEnabled', checked);
                }}
              />
              <Text strong>Sistema de niveles activo</Text>
            </Space>

            <Space>
              <Switch
                checked={autoLevelUp}
                onChange={(checked) => {
                  handleConfigChange('autoLevelUp', checked);
                }}
              />
              <Text>Subida automática de nivel cuando se cumplen requisitos</Text>
            </Space>
          </Space>
        </Card>

        <StyledCard
          title={
            <Space>
              <TrophyOutlined style={{ color: '#ac4949' }} />
              <span>Jerarquía de Niveles</span>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Crear Nivel
            </Button>
          }
        >
          {loading ? (
            <Spin />
          ) : sortedLevels.length === 0 ? (
            <Empty
              description="No hay niveles creados"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={handleCreate}>
                Crear Primer Nivel
              </Button>
            </Empty>
          ) : (
            <div style={{
              display: 'flex',
              gap: 24,
              overflowX: 'auto',
              overflowY: 'hidden',
              paddingBottom: 20,
              paddingTop: 10,
              alignItems: 'flex-end',
              scrollbarWidth: 'thin',
              scrollbarColor: theme === 'dark' ? '#434343 #1f1f1f' : '#d9d9d9 #f0f0f0'
            }}>
              {sortedLevels.map((level) => {
                // Colores según el nivel adaptados a tema oscuro/claro
                const getColorScheme = (levelNum: number, isDark: boolean) => {
                  if (levelNum === 1) {
                    return isDark
                      ? { bg: '#162312', border: '#237804', color: '#52c41a', glow: 'rgba(82, 196, 26, 0.2)' }
                      : { bg: '#f6ffed', border: '#b7eb8f', color: '#52c41a', glow: 'rgba(82, 196, 26, 0.1)' };
                  }
                  if (levelNum === 2) {
                    return isDark
                      ? { bg: '#111d2c', border: '#0050b3', color: '#1890ff', glow: 'rgba(24, 144, 255, 0.2)' }
                      : { bg: '#e6f7ff', border: '#91d5ff', color: '#1890ff', glow: 'rgba(24, 144, 255, 0.1)' };
                  }
                  if (levelNum === 3) {
                    return isDark
                      ? { bg: '#1d1028', border: '#531dab', color: '#9254de', glow: 'rgba(146, 84, 222, 0.2)' }
                      : { bg: '#f9f0ff', border: '#d3adf7', color: '#722ed1', glow: 'rgba(114, 46, 209, 0.1)' };
                  }
                  if (levelNum === 4) {
                    return isDark
                      ? { bg: '#2b1d11', border: '#ad4e00', color: '#fa8c16', glow: 'rgba(250, 140, 22, 0.2)' }
                      : { bg: '#fff7e6', border: '#ffd591', color: '#fa8c16', glow: 'rgba(250, 140, 22, 0.1)' };
                  }
                  if (levelNum >= 5) {
                    return isDark
                      ? { bg: '#2a1215', border: '#a8071a', color: '#ff4d4f', glow: 'rgba(255, 77, 79, 0.2)' }
                      : { bg: '#fff1f0', border: '#ffccc7', color: '#f5222d', glow: 'rgba(245, 34, 45, 0.1)' };
                  }
                  return isDark
                    ? { bg: '#1f1f1f', border: '#434343', color: '#8c8c8c', glow: 'rgba(140, 140, 140, 0.2)' }
                    : { bg: '#fafafa', border: '#d9d9d9', color: '#595959', glow: 'rgba(89, 89, 89, 0.1)' };
                };

                const colorScheme = getColorScheme(level.levelNumber, theme === 'dark');

                // Altura jerárquica usando vh - niveles más altos son más grandes
                const baseHeightVh = 45; // Nivel 1 es el más pequeño
                const heightIncrementVh = 10; // Cada nivel sube 10vh
                const cardHeightVh = baseHeightVh + (level.levelNumber - 1) * heightIncrementVh;
                const cardHeight = `${cardHeightVh}vh`;

                return (
                  <div key={level.id} style={{ minWidth: 300, flexShrink: 0 }}>
                    <Card
                      style={{
                        background: theme === 'dark'
                          ? `linear-gradient(135deg, ${colorScheme.bg} 0%, ${colorScheme.bg}dd 100%)`
                          : `linear-gradient(135deg, ${colorScheme.bg} 0%, ${colorScheme.bg}ee 100%)`,
                        border: `2px solid ${colorScheme.border}`,
                        borderRadius: 16,
                        position: 'relative',
                        overflow: 'visible',
                        height: cardHeight,
                        boxShadow: theme === 'dark'
                          ? `0 8px 24px ${colorScheme.glow}, 0 4px 8px rgba(0,0,0,0.4)`
                          : `0 8px 24px ${colorScheme.glow}, 0 4px 8px rgba(0,0,0,0.1)`,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                        bodyStyle={{
                          padding: 16,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                          e.currentTarget.style.boxShadow = theme === 'dark'
                            ? `0 12px 32px ${colorScheme.glow}, 0 8px 16px rgba(0,0,0,0.5)`
                            : `0 12px 32px ${colorScheme.glow}, 0 8px 16px rgba(0,0,0,0.15)`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = theme === 'dark'
                            ? `0 8px 24px ${colorScheme.glow}, 0 4px 8px rgba(0,0,0,0.4)`
                            : `0 8px 24px ${colorScheme.glow}, 0 4px 8px rgba(0,0,0,0.1)`;
                        }}
                      >
                      {/* Badge de nivel */}
                      <div style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        background: colorScheme.color,
                        color: '#fff',
                        padding: '4px 10px',
                        borderRadius: 16,
                        fontSize: 11,
                        fontWeight: 700,
                        boxShadow: `0 2px 8px ${colorScheme.color}60`,
                        letterSpacing: '0.5px'
                      }}>
                        LVL {level.levelNumber}
                      </div>

                      {/* Icono y nombre */}
                      <div style={{ textAlign: 'center', marginBottom: 16, marginTop: 4 }}>
                        <div style={{
                          fontSize: 40,
                          color: colorScheme.color,
                          marginBottom: 10,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <TrophyOutlined />
                        </div>
                        <Title level={4} style={{
                          margin: 0,
                          color: colorScheme.color,
                          fontSize: 17,
                          fontWeight: 700,
                          letterSpacing: '0.3px'
                        }}>
                          {level.name}
                        </Title>
                        {level.description && (
                          <div style={{ marginTop: 6 }}>
                            <Text type="secondary" style={{ fontSize: 12, lineHeight: '1.4' }}>
                              {level.description}
                            </Text>
                          </div>
                        )}
                      </div>

                      <Divider style={{ margin: '12px 0' }} />

                      {/* Requisitos */}
                      <div style={{ marginBottom: 16 }}>
                        <Space style={{ marginBottom: 10 }} align="center" size="small">
                          <CheckCircleOutlined style={{ color: colorScheme.color, fontSize: 14 }} />
                          <Text strong style={{ fontSize: 13, color: colorScheme.color, fontWeight: 600 }}>
                            Requisitos
                          </Text>
                        </Space>
                        <div style={{ paddingLeft: 4 }}>
                          {level.minLoadCount && (
                            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <BarChartOutlined style={{ color: colorScheme.color, fontSize: 13 }} />
                              <Text style={{ fontSize: 12 }}>{level.minLoadCount} cargas</Text>
                            </div>
                          )}
                          {level.minTotalAmount && (
                            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <DollarOutlined style={{ color: colorScheme.color, fontSize: 13 }} />
                              <Text style={{ fontSize: 12 }}>${level.minTotalAmount.toLocaleString()}</Text>
                            </div>
                          )}
                          {level.minConsecutiveDays && (
                            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <FireOutlined style={{ color: colorScheme.color, fontSize: 13 }} />
                              <Text style={{ fontSize: 12 }}>{level.minConsecutiveDays} días</Text>
                            </div>
                          )}
                          {!level.minLoadCount && !level.minTotalAmount && !level.minConsecutiveDays && (
                            <Text type="secondary" style={{ fontSize: 12 }}>Sin requisitos</Text>
                          )}
                        </div>
                      </div>

                      {/* Multiplicador */}
                      <div style={{ marginBottom: 16 }}>
                        <Space style={{ marginBottom: 8 }} align="center" size="small">
                          <ThunderboltOutlined style={{ color: colorScheme.color, fontSize: 14 }} />
                          <Text strong style={{ fontSize: 13, color: colorScheme.color, fontWeight: 600 }}>
                            Multiplicador
                          </Text>
                        </Space>
                        <div style={{ paddingLeft: 4 }}>
                          <Tag
                            color={level.baseMultiplier > 1 ? 'green' : 'default'}
                            style={{
                              fontSize: 14,
                              padding: '4px 12px',
                              borderRadius: 8,
                              fontWeight: 700
                            }}
                          >
                            x{level.baseMultiplier.toFixed(1)}
                          </Tag>
                        </div>
                      </div>

                      <div style={{ flex: 1 }} />

                      <Divider style={{ margin: '8px 0' }} />

                      {/* Acciones */}
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space>
                          <Tooltip title={level.isActive ? "Activo" : "Inactivo"}>
                            <Switch
                              size="small"
                              checked={level.isActive}
                              onChange={async (checked) => {
                                try {
                                  await gamificationApi.updateLevel(level.id, {
                                    ...level,
                                    isActive: checked
                                  });
                                  message.success(`Nivel ${checked ? 'activado' : 'desactivado'}`);
                                  loadLevels();
                                } catch (error) {
                                  message.error('Error al actualizar');
                                }
                              }}
                            />
                          </Tooltip>
                        </Space>
                        <Space>
                          <Tooltip title="Editar">
                            <Button
                              size="small"
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => handleEdit(level)}
                            />
                          </Tooltip>
                          {level.levelNumber !== 1 && (
                            <Popconfirm
                              title="¿Eliminar este nivel?"
                              onConfirm={() => handleDelete(level.id)}
                              okText="Eliminar"
                              cancelText="Cancelar"
                            >
                              <Tooltip title="Eliminar">
                                <Button
                                  size="small"
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                />
                              </Tooltip>
                            </Popconfirm>
                          )}
                        </Space>
                      </Space>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </StyledCard>
      </Space>

      <Modal
        title={editingLevel ? 'Editar Nivel' : 'Crear Nuevo Nivel'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            baseMultiplier: 1.0,
            isActive: true,
            order: levels.length + 1,
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Número de Nivel"
                name="levelNumber"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Nombre"
                name="name"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Input placeholder="Ej: Bronce" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Icono (Emoji)"
                name="icon"
              >
                <Input placeholder="🥉" maxLength={2} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Descripción"
            name="description"
          >
            <TextArea rows={2} placeholder="Descripción del nivel" />
          </Form.Item>

          <Divider>Requisitos para alcanzar</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Mínimo de cargas"
                name="minLoadCount"
                tooltip="Cantidad mínima de cargas necesarias"
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Opcional" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Monto total mínimo"
                name="minTotalAmount"
                tooltip="Monto total cargado necesario"
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Opcional" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Días consecutivos"
                name="minConsecutiveDays"
                tooltip="Racha mínima necesaria"
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Opcional" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Beneficios</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Multiplicador de Bonus"
                name="baseMultiplier"
                rules={[{ required: true, message: 'Requerido' }]}
                tooltip="1.0 = normal, 1.2 = +20% de bonus"
              >
                <InputNumber
                  min={1}
                  max={3}
                  step={0.1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Orden de visualización"
                name="order"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                {editingLevel ? 'Actualizar' : 'Crear'} Nivel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
